import React, { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

/**
 * GlobalAdBanner - A fixed-position ad banner that appears on every page of the website.
 * Supports multiple rotating ads with internal and external link navigation.
 * Now with real-time updates from Firebase.
 */
const GlobalAdBanner = () => {
    const [ads, setAds] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isVisible, setIsVisible] = useState(true);
    const [isDismissed, setIsDismissed] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const navigate = useNavigate();

    // Load ads from localStorage as fallback
    const loadLocalAds = useCallback(() => {
        // Check if ad was dismissed in this session
        const dismissed = sessionStorage.getItem('globalAdDismissed');
        if (dismissed) {
            setIsDismissed(true);
            return [];
        }

        // Try new multi-ad format first
        const storedAds = localStorage.getItem('siteAds');
        if (storedAds) {
            try {
                const adsData = JSON.parse(storedAds);
                if (Array.isArray(adsData)) {
                    return adsData.filter(ad => ad.active && ad.url);
                }
            } catch (e) {
                console.error('Error parsing ads data:', e);
            }
        }

        // Fallback to legacy single ad format
        const storedAd = localStorage.getItem('activeAd');
        if (storedAd) {
            try {
                const adData = JSON.parse(storedAd);
                if (adData.active) {
                    return [{
                        id: 1,
                        active: true,
                        type: adData.type || 'image',
                        url: adData.url,
                        linkType: adData.isInternal ? 'internal' : 'external',
                        internalPath: adData.isInternal ? adData.link : '/about/careers',
                        externalLink: !adData.isInternal ? adData.link : '',
                        title: adData.title || ''
                    }];
                }
            } catch (e) {
                console.error('Error parsing legacy ad data:', e);
            }
        }
        return [];
    }, []);

    useEffect(() => {
        // Check if dismissed
        const dismissed = sessionStorage.getItem('globalAdDismissed');
        if (dismissed) {
            setIsDismissed(true);
            return;
        }

        // Subscribe to real-time ad updates from Firebase
        const unsubscribe = api.ads.subscribeToAds((adsData) => {
            if (adsData && adsData.length > 0) {
                // Filter active ads with URLs
                const activeAds = adsData.filter(ad => ad.active && ad.url);
                if (activeAds.length > 0) {
                    setAds(activeAds);
                    setIsDismissed(false);
                    return;
                }
            }
            // Fallback to localStorage if no Firebase ads
            const localAds = loadLocalAds();
            if (localAds.length > 0) {
                setAds(localAds);
                setIsDismissed(false);
            }
        }, true); // true = active only

        // Cleanup subscription on unmount
        return () => {
            if (typeof unsubscribe === 'function') {
                unsubscribe();
            }
        };
    }, [loadLocalAds]);

    // Rotate ads every 4 seconds
    useEffect(() => {
        if (ads.length <= 1) return;

        const rotationInterval = setInterval(() => {
            setIsAnimating(true);
            setTimeout(() => {
                setCurrentIndex(prev => (prev + 1) % ads.length);
                setIsAnimating(false);
            }, 300); // Animation duration
        }, 4000);

        return () => clearInterval(rotationInterval);
    }, [ads.length]);

    const handleClose = () => {
        setIsVisible(false);
        sessionStorage.setItem('globalAdDismissed', 'true');
        setTimeout(() => setIsDismissed(true), 300);
    };

    const handleAdClick = () => {
        const currentAd = ads[currentIndex];
        if (!currentAd) return;

        if (currentAd.linkType === 'internal' && currentAd.internalPath) {
            // Navigate to internal page
            navigate(currentAd.internalPath);
        } else if (currentAd.linkType === 'external' && currentAd.externalLink) {
            // Open external link in new tab
            window.open(currentAd.externalLink, '_blank', 'noopener,noreferrer');
        }
    };

    if (isDismissed || ads.length === 0) return null;

    const currentAd = ads[currentIndex];
    const hasLink = (currentAd.linkType === 'internal' && currentAd.internalPath) ||
        (currentAd.linkType === 'external' && currentAd.externalLink);

    return (
        <div
            className={`fixed right-4 md:right-8 top-1/2 -translate-y-1/2 z-[9999] transition-all duration-300 hidden md:block ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                }`}
            style={{ pointerEvents: 'auto' }}
        >
            {/* Ad Container */}
            <div className="relative bg-white/95 backdrop-blur-md rounded-xl overflow-hidden shadow-2xl border border-gray-200 max-w-[320px]">
                {/* Close Button */}
                <button
                    onClick={handleClose}
                    className="absolute top-2 right-2 z-20 bg-black/60 hover:bg-black/80 text-white rounded-full p-1.5 transition-colors shadow-lg"
                    aria-label="Close ad"
                >
                    <X size={16} />
                </button>

                {/* Ad Label */}
                <div className="absolute top-2 left-2 z-20 bg-[#2A9D8F] text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider">
                    Ad
                </div>

                {/* Ad Content with Animation */}
                <div
                    className={`${hasLink ? 'cursor-pointer' : ''} transition-all duration-300 ${isAnimating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
                        }`}
                    onClick={handleAdClick}
                >
                    {currentAd.type === 'video' ? (
                        <video
                            key={currentAd.id}
                            src={currentAd.url}
                            autoPlay
                            muted
                            loop
                            playsInline
                            className="w-full h-auto max-h-[400px] object-cover"
                        />
                    ) : (
                        <img
                            key={currentAd.id}
                            src={currentAd.url}
                            alt={currentAd.title || 'Advertisement'}
                            className="w-full h-auto max-h-[400px] object-cover"
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />
                    )}
                </div>

                {/* Ad Title/CTA */}
                {currentAd.title && (
                    <div
                        className={`p-3 bg-gradient-to-t from-black/80 via-black/50 to-transparent absolute bottom-0 left-0 right-0 transition-all duration-300 ${isAnimating ? 'opacity-0' : 'opacity-100'
                            }`}
                    >
                        <p className="text-white text-sm font-medium text-center drop-shadow-lg">{currentAd.title}</p>
                        {hasLink && (
                            <p className="text-white/70 text-[10px] text-center mt-1">
                                Click to {currentAd.linkType === 'internal' ? 'go to page' : 'learn more'}
                            </p>
                        )}
                    </div>
                )}

                {/* Progress/Rotation Indicator (for multiple ads) */}
                {ads.length > 1 && (
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                        {ads.map((_, idx) => (
                            <button
                                key={idx}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setCurrentIndex(idx);
                                }}
                                className={`transition-all duration-300 rounded-full ${idx === currentIndex
                                    ? 'bg-white w-4 h-1.5'
                                    : 'bg-white/50 w-1.5 h-1.5 hover:bg-white/70'
                                    }`}
                                aria-label={`Go to ad ${idx + 1}`}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Navigation hint for internal links */}
            {hasLink && currentAd.linkType === 'internal' && (
                <div className="mt-2 text-center">
                    <span className="text-xs bg-white/90 text-gray-600 px-2 py-1 rounded-full shadow-sm">
                        → {currentAd.internalPath}
                    </span>
                </div>
            )}
        </div>
    );
};

export default GlobalAdBanner;
